import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchEmployeeData } from '../../../../redux/slice/masterSlice';
const { TextArea } = Input;
const { Option } = Select;


interface DepartmentItem {
    department_id: number;
    department_name: string;
    department_status: number;
}


interface DesignationItem {
    designation_id: number;
    designation_name: string;
    designation_status: number;
}

const employeeTypes = [
    { label: 'Contractor', value: 'contractor' },
    { label: 'Company Employee', value: 'company employee' },
];


interface EmployeeModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void;
    initialValues?: {
        employee_id?: number;
        employee_fname?: string;
        employee_lname?: string;
        employee_mobile?: string;
        employee_department?: number;
        employee_type?: string;
        employee_salary?: number;
        employee_designation?: string;
        employee_address?: string;
    } | null;
    isEditMode?: boolean;
}

const EmployeeModel: React.FC<EmployeeModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {

    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);


    const [designations, setDesignation] = useState<DesignationItem[]>([]);
    const [departments, setDepartments] = useState<DepartmentItem[]>([]);

    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };

    // Submit handler
    const handleAddEmployee = async (values: any) => {
        try {
            const dataVal = {
                ...values,
                ...(initialValues?.employee_id && {
                    employee_id: initialValues.employee_id,
                }),
            };

            const url = isEditMode ? 'edit-employee' : 'add-employee';
            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });

            onCreate(data);
            showSuccess(
                isEditMode
                    ? 'Employee updated successfully!'
                    : 'Employee created successfully!'
            );

            form.resetFields();

            if (isModalClose) {
                onCancel();
            }
            dispatch(fetchEmployeeData());
        } catch (error: any) {
            showError(
                error?.response?.data?.message ||
                `Failed to ${isEditMode ? 'update' : 'create'} employee`
            );
        }
    };

    // ✅ Fetch designation list when component mounts
    useEffect(() => {
        const fetchDesignations = async () => {
            try {
                const designationData = await apiRequest<DesignationItem[]>({
                    method: 'get',
                    url: 'designation?status=1',
                });
                setDesignation(designationData);
            } catch (error) {
                showError('Failed to fetch designations');
            }
        };

        fetchDesignations();
    }, []);

    // Fetch from department master
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const departmentData = await apiRequest<DepartmentItem[]>({
                    method: 'get',
                    url: 'departments?status=1', // update URL according to your API
                });
                setDepartments(departmentData);
            } catch (error) {
                showError('Failed to fetch departments');
            }
        };

        fetchDepartments();
    }, []);


    // Set initial values
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue(initialValues);
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);

    return (
        <Modal
            title={isEditMode ? 'Edit Employee' : 'Add New Employee'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
            centered
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddEmployee}
                autoComplete="off"
            >
                <Row gutter={16}>
                    {/* First Name */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_fname"
                            label="First Name"
                            rules={[
                                { required: true, message: 'Enter first name' },
                            ]}
                        >
                            <Form.Item noStyle dependencies={['employee_fname']}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const currentValue = getFieldValue('employee_fname') || '';

                                    const handleChange = (
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        let value = e.target.value;

                                        // ✅ allow only alphabets
                                        value = value.replace(/[^a-zA-Z]/g, '');

                                        // ✅ limit to 50 characters
                                        value = value.slice(0, 50);

                                        // ✅ capitalize first letter
                                        if (value.length > 0) {
                                            value = value.charAt(0).toUpperCase() + value.slice(1);
                                        }

                                        setFieldsValue({ employee_fname: value });
                                    };

                                    return (
                                        <Input
                                            value={currentValue}
                                            maxLength={50}
                                            onChange={handleChange}
                                            placeholder="Enter first name"
                                            suffix={
                                                <span style={{ fontSize: '12px', color: '#888' }}>
                                                    {currentValue.length}/50
                                                </span>
                                            }
                                        />
                                    );
                                }}
                            </Form.Item>
                        </Form.Item>
                    </Col>

                    {/* Last Name */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_lname"
                            label="Last Name"
                            rules={[
                                { required: true, message: 'Enter last name' },
                            ]}
                        >
                            <Form.Item noStyle dependencies={['employee_lname']}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const currentValue = getFieldValue('employee_lname') || '';

                                    const handleChange = (
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        let value = e.target.value;

                                        // ✅ allow only alphabets
                                        value = value.replace(/[^a-zA-Z]/g, '');

                                        // ✅ limit to 50 characters
                                        value = value.slice(0, 50);

                                        // ✅ capitalize first letter
                                        if (value.length > 0) {
                                            value = value.charAt(0).toUpperCase() + value.slice(1);
                                        }

                                        setFieldsValue({ employee_lname: value });
                                    };

                                    return (
                                        <Input
                                            value={currentValue}
                                            maxLength={50}
                                            onChange={handleChange}
                                            placeholder="Enter last name"
                                            suffix={
                                                <span style={{ fontSize: '12px', color: '#888' }}>
                                                    {currentValue.length}/50
                                                </span>
                                            }
                                        />
                                    );
                                }}
                            </Form.Item>
                        </Form.Item>
                    </Col>

                    {/* Mobile NUmber */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_mobile"
                            label="Mobile Number"
                            rules={[
                                { required: true, message: 'Required' },
                                {
                                    pattern: /^[0-9]{10}$/,
                                    message: 'Mobile number must be 10 digits',
                                },
                            ]}
                        >
                            <Form.Item noStyle dependencies={['employee_mobile']}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const currentValue = getFieldValue('employee_mobile') || '';

                                    const handleChange = (
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        let value = e.target.value;

                                        // ✅ allow only digits
                                        value = value.replace(/[^0-9]/g, '');

                                        // ✅ limit to 10 digits
                                        value = value.slice(0, 10);

                                        setFieldsValue({ employee_mobile: value });
                                    };

                                    const handleBlur = () => {
                                        const value = getFieldValue('employee_mobile') || '';
                                        if (!value) {
                                            setFieldsValue({ employee_mobile: '' });
                                        }
                                    };

                                    return (
                                        <Input
                                            value={currentValue}
                                            maxLength={10}
                                            type="tel"
                                            placeholder="Enter mobile number"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            suffix={
                                                <span style={{ fontSize: '12px', color: '#888' }}>
                                                    {currentValue.length}/10
                                                </span>
                                            }
                                        />
                                    );
                                }}
                            </Form.Item>
                        </Form.Item>
                    </Col>


                    <Col span={8}>
                        <div style={{ marginBottom: 8 }}>Department</div>

                        <Form.Item
                            name="employee_department"
                            rules={[{ required: true, message: 'Select a department!' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select Department"
                                allowClear
                                style={{ width: '100%' }}
                                value={form.getFieldValue('employee_department')}
                                onChange={(value) => {
                                    const selected = departments.find((d) => d.department_id === value);
                                    if (selected) {
                                        form.setFieldsValue({
                                            employee_department: selected.department_id, // or name if backend expects it
                                        });
                                    } else {
                                        form.setFieldsValue({ employee_department: null });
                                    }
                                }}
                                options={[
                                    { label: 'Select Department', value: null },
                                    ...departments.map((d) => ({
                                        label: d.department_name,
                                        value: d.department_id,
                                    })),
                                ]}
                                filterOption={(input, option) =>
                                    (option?.label as string)
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                                dropdownMatchSelectWidth={false}
                            />
                        </Form.Item>
                    </Col>

                    {/* Type */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_type"
                            label="Employee Type"
                            rules={[{ required: true, message: 'Select employee type' }]}
                        >
                            <Select placeholder="Select Type" showSearch>
                                {employeeTypes.map((type) => (
                                    <Option key={type.value} value={type.value}>
                                        {type.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* salary */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_salary"
                            label="Salary"
                            rules={[
                                { required: true, message: 'Enter salary' },
                                {
                                    validator: (_, value) => {
                                        if (value === undefined || value === null || value === '') {
                                            return Promise.reject('Salary is required');
                                        }
                                        const num = parseFloat(value);
                                        if (isNaN(num)) {
                                            return Promise.reject('Salary must be a number');
                                        }
                                        if (num < 0) {
                                            return Promise.reject('Salary cannot be less than 0');
                                        }
                                        if (num > 99999999.99) {
                                            return Promise.reject('Salary cannot exceed 99,999,999.99');
                                        }
                                        if (!/^\d+(\.\d{0,2})?$/.test(value.toString())) {
                                            return Promise.reject('Salary can have up to 2 decimal places');
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input
                                type="text"
                                placeholder="Salary"
                                onChange={(e) => {
                                    let value = e.target.value;

                                    // Remove invalid characters (only digits and dot)
                                    value = value.replace(/[^0-9.]/g, '');

                                    // Allow only 2 decimal places
                                    const parts = value.split('.');
                                    if (parts[1]?.length > 2) {
                                        parts[1] = parts[1].slice(0, 2);
                                        value = parts.join('.');
                                    }

                                    // Prevent value > 99999999.99
                                    if (parseFloat(value) > 99999999.99) {
                                        value = '99999999.99';
                                    }

                                    form.setFieldsValue({ employee_salary: value });
                                }}
                                suffix={
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        0 - 99,999,999.99
                                    </span>
                                }
                            />
                        </Form.Item>
                    </Col>


                    {/* Designation */}
                    <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 8 }}>Designation</div>

                        <Form.Item
                            name="employee_designation"
                            rules={[{ required: true, message: 'Please select a designation!' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select Designation"
                                allowClear
                                style={{ width: '100%' }}
                                value={form.getFieldValue('employee_designation')}
                                onChange={(value) => {
                                    if (value) {
                                        form.setFieldsValue({
                                            employee_designation: value, // ✅ store ID, not name
                                        });
                                    } else {
                                        form.setFieldsValue({ employee_designation: null });
                                    }
                                }}
                                options={[
                                    { label: 'Select Designation', value: null },
                                    ...designations.map((d) => ({
                                        label: d.designation_name,
                                        value: d.designation_id, // ✅ value is ID
                                    })),
                                ]}
                                filterOption={(input, option) =>
                                    (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                }
                                dropdownMatchSelectWidth={false}
                            />
                        </Form.Item>
                    </Col>


                    {/*address */}
                    {/* Address with character count */}
                    <Col span={16}>
                        <Form.Item
                            name="employee_address"
                            label="Address"
                            rules={[{ required: true, message: 'Enter address' }]}
                        >
                            <Form.Item noStyle dependencies={['employee_address']}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const currentValue = getFieldValue('employee_address') || '';

                                    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                        let value = e.target.value;

                                        // Limit to 200 characters (adjust as needed)
                                        value = value.slice(0, 200);

                                        setFieldsValue({ employee_address: value });
                                    };

                                    return (
                                        <TextArea
                                            rows={3}
                                            value={currentValue}
                                            onChange={handleChange}
                                            placeholder="Address"
                                        />
                                    );
                                }}
                            </Form.Item>
                            <div style={{ fontSize: '12px', color: '#888', textAlign: 'right' }}>
                                {form.getFieldValue('employee_address')?.length || 0}/200
                            </div>
                        </Form.Item>
                    </Col>

                </Row>

                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>

                        {!isEditMode && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(false)}
                                disabled={isSubmitDisabled()}
                            >
                                Submit & Create New
                            </Button>
                        )}

                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EmployeeModel;
